import React from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App.jsx';
import { initViewport } from './ios/viewport.js';
import { initTheme } from './theme/themeMode.js';
import './theme.css';

initViewport();
// Before render: applying the stored theme inside a component flashes the wrong
// palette on every load.
initTheme();
/*
 * SERVICE WORKER, AND WHY IT RELOADS ITSELF.
 *
 * `registerType: 'autoUpdate'` installs a new worker and calls skipWaiting, so it takes
 * control promptly. What it does NOT do is refresh the page that is already open, and the
 * page has already booted from the OLD precached `index.html`, which names the OLD asset
 * hashes. So every load showed the PREVIOUS deploy and you had to load twice to see a
 * change.
 *
 * That is not a theoretical rough edge. It cost real time: several fixes were verified
 * correct on the server and reported as shipped while the owner's browser was pinned to a
 * build from before them, and the natural conclusion was that the wrong version had been
 * pushed. Measured directly: the tab had `index-DUOAOK0b.css` while the server was
 * serving `index-CdVrMd1a.css`.
 *
 * `controllerchange` fires when the new worker claims this page, which is exactly the
 * moment the assets on screen became stale, so that is when to reload.
 *
 * Two guards, both necessary:
 *  - `hadController` skips the FIRST-EVER install, where the worker claims a page that
 *    was loaded from the network and is already current. Reloading there would make every
 *    first visit flash for no reason.
 *  - `reloaded` makes it once-only. A reload loop on the landing view would be worse than
 *    the staleness it fixes.
 */
registerSW({
  immediate: true,
  onRegisteredSW(_url, registration) {
    if (!registration) return;
    const check = (): void => void registration.update();

    // An installed iOS home-screen app is almost never "closed", so it can run for weeks
    // without a natural update check. Hourly is cheap (one conditional GET) and means a
    // deploy reaches the phone without being force-quit.
    setInterval(check, 60 * 60 * 1000);

    /*
     * AND on waking, which is what the timer alone could not cover.
     *
     * A timer does not run in a suspended iOS tab. He keeps dozens of tabs open, so the MO OS
     * tab is nearly always suspended: Safari restores it from cache, the interval that would
     * have fired never did, and the page comes back on whatever bundle it was frozen with. That
     * is exactly what happened on 2026-08-05, when a fix verified on the server was still not
     * visible on his phone and the screenshot looked like the fix had failed.
     *
     * Both events, because they cover different restores: `visibilitychange` is a tab being
     * switched back to, and `pageshow` with `persisted` is a back-forward-cache restore, where
     * no other lifecycle event fires at all.
     */
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    globalThis.addEventListener('pageshow', (e) => {
      if ((e as PageTransitionEvent).persisted) check();
    });
  },
});

if ('serviceWorker' in navigator) {
  const hadController = navigator.serviceWorker.controller !== null;
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloaded) return;
    reloaded = true;
    globalThis.location.reload();
  });
}

const root = createRoot(document.getElementById('root')!);

// No login-free guest view is rendered here (see GuestShare.tsx, kept on disk
// unreferenced for upstream-merge reasons; docs/superpowers/specs/
// 2026-07-28-mo-os-a1-rehome-design.md, WI-3). Share links only work for the
// authenticated owner, through the normal app below.
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

/*
 * Retire the pre-JS splash now that React has painted.
 *
 * Removed rather than hidden: it is `position: fixed` at z-index 9999, so a splash left in the tree
 * with `display:none` is one CSS mistake away from covering the whole app. `requestAnimationFrame`
 * so the removal lands after the first React frame and the screen never flashes empty between them.
 */
requestAnimationFrame(() => {
  document.getElementById('boot-splash')?.remove();
});
